import * as Yup from 'yup';
import { canCurrentUser } from '@utils/Permissions';
import { Capability } from '@utils/Permissions/Capabilities';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    TextField,
    Typography,
    Checkbox,
    FormControlLabel,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Switch,
    Collapse
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { OpenModalHookType } from '@hooks/useOpenModal';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import CustomAutocomplete from '@components/Utilities/CustomAutocomplete';
import { ExpandMore } from '@mui/icons-material';
import { Map, Marker, useMapsLibrary } from '@vis.gl/react-google-maps';
import { BranchOffice } from '@api/BranchOffices/BranchOffice';
import { getStockDays, STOCK_DAYS_DEFAULT } from '@api/Inputs/StockStatus';

interface ModalSaveBranchOfficeProps {
    modalAddBranchOffice: OpenModalHookType;
    branchOffice?: BranchOffice;
    onAddBranchOffice: (branchOffice: BranchOffice) => void;
}

const daysOfWeek = [
    { id: 'monday', label: 'Lunes' },
    { id: 'tuesday', label: 'Martes' },
    { id: 'wednesday', label: 'Miércoles' },
    { id: 'thursday', label: 'Jueves' },
    { id: 'friday', label: 'Viernes' },
    { id: 'saturday', label: 'Sábado' },
    { id: 'sunday', label: 'Domingo' }
];

const saveBranchOfficeSchema = Yup.object({
    uid: Yup.string(),
    number: Yup.string().required('Numero es requerido'),
    name: Yup.string().required('Nombre es requerido'),
    address: Yup.string().required('Dirección es requerida'),
    addressDetails: Yup.object()
        .shape({
            street: Yup.string(),
            city: Yup.string(),
            state: Yup.string(),
            country: Yup.string(),
            zipCode: Yup.string()
        })
        .nullable(),
    googlePlaceId: Yup.string(),
    location: Yup.object().shape({
        lat: Yup.number().required(),
        lng: Yup.number().required()
    }),
    phone: Yup.string().required('Número de teléfono es requerido'),
    distributionCenterId: Yup.string().required('Cedis es requerido'),
    hasPickup: Yup.boolean().default(false),
    hasDelivery: Yup.boolean().default(false),
    delivery: Yup.object().when('hasDelivery', ([hasDelivery], schema) => {
        return hasDelivery
            ? schema.shape({
                  initialFee: Yup.number()
                      .required('Tarifa inicial es requerida')
                      .min(0, 'Tarifa no puede ser negativa'),
                  costPerKm: Yup.number().required('Costo por km es requerido').min(0, 'Costo no puede ser negativo'),
                  maxDistance: Yup.number()
                      .required('Límite de distancia es requerido')
                      .min(0, 'Distancia no puede ser negativa'),
                  freeDeliveryEnabled: Yup.boolean(),
                  minAmountForFreeDelivery: Yup.number().when(
                      'freeDeliveryEnabled',
                      ([freeDeliveryEnabled], schema) => {
                          return freeDeliveryEnabled
                              ? schema
                                    .required('Monto mínimo es requerido')
                                    .min(0, 'El monto no puede ser negativo')
                                    .typeError('Debe ser un número')
                              : schema.notRequired().nullable();
                      }
                  )
              })
            : schema.notRequired();
    }),
    schedule: Yup.object().shape(
        daysOfWeek.reduce((acc, day) => {
            acc[day.id] = Yup.object().shape({
                enabled: Yup.boolean(),
                openingTime: Yup.string().when('enabled', ([enabled], schema) => {
                    return enabled ? schema.required('Hora de apertura es requerida') : schema;
                }),
                closingTime: Yup.string().when('enabled', ([enabled], schema) => {
                    return enabled ? schema.required('Hora de cierre es requerida') : schema;
                })
            });
            return acc;
        }, {} as any)
    ),
    manager: Yup.object().shape({
        name: Yup.string().required('Nombre es requerido'),
        phone: Yup.string().required('Número de teléfono es requerido'),
        email: Yup.string().required('Correo es requerido')
    }),
    notifyManagerOnNewOrder: Yup.boolean().default(true),
    inventoryDiscountEnabled: Yup.boolean().default(false),
    // Días de inventario para Crítico y Bajo (plan de inventario F4): los ajusta la sucursal
    stockDays: Yup.object().shape({
        critical: Yup.number().typeError('Escribe un número de días').min(0, 'Mínimo 0').required('Días para Crítico'),
        low: Yup.number().typeError('Escribe un número de días').min(0, 'Mínimo 0').required('Días para Bajo')
    })
});

export const ModalSaveBranchOffice: React.FunctionComponent<ModalSaveBranchOfficeProps> = (props: any) => {
    const { modalAddBranchOffice, branchOffice, onAddBranchOffice } = props;
    // Distribution centers come from the shared catalog slice (single source of truth).
    const { distributionCenters } = useSelector((state: RootReducer) => state.krispis);
    // Número, CEDIS e inventario son del admin general (§8.7): el admin de sucursal los
    // ve y no los cambia. El servicio los vuelve a proteger (`keepProtectedBranchFields`).
    const lockProtected = !canCurrentUser(Capability.BranchEditProtected);

    // Estados para el mapa
    const [showMap, setShowMap] = useState(false);
    const [mapCenter, setMapCenter] = useState({ lat: 0, lng: 0 });

    // Librería de Geocoding para Reverse Geocoding
    const geocodingLib = useMapsLibrary('geocoding');

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors }
    } = useForm<BranchOffice>({
        resolver: yupResolver(saveBranchOfficeSchema),
        defaultValues: {
            hasPickup: false,
            hasDelivery: false,
            notifyManagerOnNewOrder: true,
            inventoryDiscountEnabled: false,
            stockDays: STOCK_DAYS_DEFAULT,
            location: {
                lat: 0,
                lng: 0
            },
            addressDetails: {
                street: '',
                city: '',
                state: '',
                country: '',
                zipCode: ''
            },
            delivery: {
                initialFee: 0,
                costPerKm: 0,
                maxDistance: 0,
                freeDeliveryEnabled: false,
                minAmountForFreeDelivery: 0
            },
            schedule: daysOfWeek.reduce((acc, day) => {
                acc[day.id] = { enabled: false, openingTime: '', closingTime: '' };
                return acc;
            }, {} as any)
        }
    });

    const hasDelivery = watch('hasDelivery');
    const freeDeliveryEnabled = watch('delivery.freeDeliveryEnabled');

    // Observamos la ubicación para renderizar el marcador
    const locationLat = watch('location.lat');
    const locationLng = watch('location.lng');

    const handleClose = () => {
        modalAddBranchOffice.toggle();
    };

    const onSaveBranchOffice: SubmitHandler<BranchOffice> = async (values) => {
        if (!values.hasDelivery) {
            values.delivery = undefined;
        } else {
            if (!values.delivery?.freeDeliveryEnabled) {
                if (values.delivery) values.delivery.minAmountForFreeDelivery = 0;
            }
        }
        onAddBranchOffice(values);
    };

    // FUNCIÓN: Manejar el arrastre del marcador (Reverse Geocoding)
    const handleMarkerDragEnd = async (e: any) => {
        if (!e.latLng || !geocodingLib) return;

        const newLat = e.latLng.lat();
        const newLng = e.latLng.lng();

        // 1. Actualizamos la ubicación (coordenadas)
        setValue('location', { lat: newLat, lng: newLng });

        const geocoder = new geocodingLib.Geocoder();

        try {
            // 2. Buscamos la dirección a partir de las coordenadas
            const response = await geocoder.geocode({ location: { lat: newLat, lng: newLng } });

            if (response.results && response.results.length > 0) {
                const place = response.results[0];

                // 3. Actualizamos la dirección y el Place ID en el formulario
                setValue('address', place.formatted_address || '');
                setValue('googlePlaceId', place.place_id || '');

                // 4. Extraemos y actualizamos los detalles de la dirección
                if (place.address_components) {
                    let street = '';
                    let streetNumber = '';
                    let city = '';
                    let state = '';
                    let country = '';
                    let zipCode = '';

                    place.address_components.forEach((component) => {
                        const types = component.types;
                        if (types.includes('route')) street = component.long_name;
                        if (types.includes('street_number')) streetNumber = component.long_name;
                        if (types.includes('locality') || types.includes('sublocality')) city = component.long_name;
                        if (types.includes('administrative_area_level_1')) state = component.long_name;
                        if (types.includes('country')) country = component.long_name;
                        if (types.includes('postal_code')) zipCode = component.long_name;
                    });

                    setValue('addressDetails', {
                        street: `${street} ${streetNumber}`.trim(),
                        city,
                        state,
                        country,
                        zipCode
                    });
                }
            }
        } catch (error) {
            console.error('Error al obtener la dirección desde el mapa:', error);
            // Opcional: mostrar un Toast o Alert informando que no se pudo obtener la dirección
        }
    };

    useEffect(() => {
        if (!hasDelivery) {
            setValue('delivery', {
                initialFee: 0,
                costPerKm: 0,
                maxDistance: 0,
                freeDeliveryEnabled: false,
                minAmountForFreeDelivery: 0
            });
        }
    }, [hasDelivery, setValue]);

    useEffect(() => {
        if (modalAddBranchOffice.modalOpen) {
            const initialLocation = branchOffice?.location || { lat: 0, lng: 0 };

            setMapCenter(initialLocation);
            setShowMap(false);

            reset({
                id: branchOffice?.id || undefined,
                number: branchOffice?.number || '',
                name: branchOffice?.name || '',
                address: branchOffice?.address || '',
                addressDetails: branchOffice?.addressDetails || {
                    street: '',
                    city: '',
                    state: '',
                    country: '',
                    zipCode: ''
                },
                googlePlaceId: branchOffice?.googlePlaceId || '',
                location: initialLocation,
                phone: branchOffice?.phone || '',
                distributionCenterId: branchOffice?.distributionCenterId || '',
                hasPickup: branchOffice?.hasPickup || false,
                hasDelivery: branchOffice?.hasDelivery || false,
                delivery: branchOffice?.delivery || {
                    initialFee: 0,
                    costPerKm: 0,
                    maxDistance: 0,
                    freeDeliveryEnabled: false,
                    minAmountForFreeDelivery: 0
                },
                schedule:
                    branchOffice?.schedule ||
                    daysOfWeek.reduce((acc, day) => {
                        acc[day.id] = { enabled: false, openingTime: '', closingTime: '' };
                        return acc;
                    }, {} as any),
                manager: {
                    name: branchOffice?.manager.name || '',
                    phone: branchOffice?.manager.phone || '',
                    email: branchOffice?.manager.email || ''
                },
                notifyManagerOnNewOrder: branchOffice?.notifyManagerOnNewOrder ?? true,
                inventoryDiscountEnabled: branchOffice?.inventoryDiscountEnabled ?? false,
                stockDays: getStockDays(branchOffice)
            });
        }
    }, [modalAddBranchOffice.modalOpen]);

    return (
        <Dialog
            PaperProps={{ style: { borderRadius: 15, padding: 4 } }}
            open={modalAddBranchOffice.modalOpen}
            onClose={handleClose}
            aria-labelledby="modal-save-branch-office-title"
            aria-describedby="modal-save-branch-office-description"
            fullWidth
            maxWidth="md"
        >
            <DialogTitle id="modal-save-branch-office-title">
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={600}>
                        {branchOffice?.id ? 'Editar sucursal' : 'Nueva sucursal'}
                    </Typography>
                    <IconButton color="secondary" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <form id="add-branch-office-form" onSubmit={handleSubmit(onSaveBranchOffice)}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2">Completa los datos de la nueva sucursal.</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Controller
                                name="name"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        required
                                        autoFocus
                                        label="Nombre"
                                        variant="outlined"
                                        autoComplete="firstname"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.name}
                                        helperText={errors.name && errors.name.message}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={8}>
                            <Controller
                                name="address"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <CustomAutocomplete
                                        value={field.value}
                                        onChange={(value) => {
                                            field.onChange(value);
                                        }}
                                        onPlaceSelect={(place) => {
                                            if (place) {
                                                setValue('address', place.formatted_address || '');
                                                setValue('googlePlaceId', place.place_id || '');

                                                if (place.geometry?.location) {
                                                    const newLat = place.geometry.location.lat();
                                                    const newLng = place.geometry.location.lng();

                                                    setValue('location', {
                                                        lat: newLat,
                                                        lng: newLng
                                                    });

                                                    setMapCenter({ lat: newLat, lng: newLng });
                                                }

                                                if (place.address_components) {
                                                    let street = '';
                                                    let streetNumber = '';
                                                    let city = '';
                                                    let state = '';
                                                    let country = '';
                                                    let zipCode = '';

                                                    place.address_components.forEach((component) => {
                                                        const types = component.types;
                                                        if (types.includes('route')) street = component.long_name;
                                                        if (types.includes('street_number'))
                                                            streetNumber = component.long_name;
                                                        if (types.includes('locality') || types.includes('sublocality'))
                                                            city = component.long_name;
                                                        if (types.includes('administrative_area_level_1'))
                                                            state = component.long_name;
                                                        if (types.includes('country')) country = component.long_name;
                                                        if (types.includes('postal_code'))
                                                            zipCode = component.long_name;
                                                    });

                                                    setValue('addressDetails', {
                                                        street: `${street} ${streetNumber}`.trim(),
                                                        city,
                                                        state,
                                                        country,
                                                        zipCode
                                                    });
                                                }
                                            }
                                        }}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        label="Dirección"
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showMap}
                                        onChange={(e) => setShowMap(e.target.checked)}
                                        color="primary"
                                        disabled={locationLat === 0 && locationLng === 0}
                                    />
                                }
                                label="Ajustar ubicación exacta en el mapa"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Collapse in={showMap}>
                                {locationLat !== 0 && locationLng !== 0 && (
                                    <>
                                        <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                                            * Puedes arrastrar el marcador rojo en el mapa para especificar la ubicación
                                            exacta de la sucursal. La dirección se actualizará automáticamente.
                                        </Typography>
                                        <Box
                                            height={250}
                                            width="100%"
                                            borderRadius={2}
                                            overflow="hidden"
                                            border="1px solid #ccc"
                                        >
                                            <Map
                                                defaultZoom={16}
                                                center={mapCenter}
                                                onCameraChanged={(ev) => setMapCenter(ev.detail.center)}
                                                disableDefaultUI={false}
                                                mapId="DEMO_MAP_ID"
                                            >
                                                <Marker
                                                    position={{ lat: locationLat, lng: locationLng }}
                                                    draggable={true}
                                                    onDragEnd={handleMarkerDragEnd}
                                                />
                                            </Map>
                                        </Box>
                                    </>
                                )}
                            </Collapse>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Controller
                                name="phone"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        required
                                        label="Número de teléfono"
                                        variant="outlined"
                                        autoComplete="phone"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.phone}
                                        helperText={errors.phone && errors.phone.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel id="distribution-center-select-label">CEDIS</InputLabel>
                                <Controller
                                    name="distributionCenterId"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <Select
                                            labelId="distribution-center-select-label"
                                            id="distribution-center-select"
                                            value={value}
                                            label="CEDIS"
                                            onChange={onChange}
                                            disabled={lockProtected}
                                            error={!!errors.distributionCenterId}
                                        >
                                            {distributionCenters.map((distributionCenter, index) => (
                                                <MenuItem key={index} value={distributionCenter.id}>
                                                    {distributionCenter.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Controller
                                name="number"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        required
                                        label="Número de sucursal"
                                        variant="outlined"
                                        autoComplete="number"
                                        value={value}
                                        disabled={lockProtected}
                                        onChange={onChange}
                                        error={!!errors.number}
                                        helperText={errors.number && errors.number.message}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle2">Servicios habilitados</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="hasPickup"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <FormControlLabel
                                        control={<Checkbox checked={value} onChange={onChange} color="primary" />}
                                        label="Recoger en tienda (Pickup)"
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="hasDelivery"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <FormControlLabel
                                        control={<Checkbox checked={value} onChange={onChange} color="primary" />}
                                        label="Entrega a domicilio (Delivery)"
                                    />
                                )}
                            />
                        </Grid>

                        {hasDelivery && (
                            <>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2">Configuración de Delivery</Typography>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="delivery.initialFee"
                                        control={control}
                                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                                            <TextField
                                                fullWidth
                                                required
                                                label="Tarifa inicial"
                                                type="number"
                                                variant="outlined"
                                                value={value === 0 && !value ? '' : value}
                                                onChange={onChange}
                                                error={!!error}
                                                helperText={error?.message}
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="delivery.costPerKm"
                                        control={control}
                                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                                            <TextField
                                                fullWidth
                                                required
                                                label="Costo por kilómetro"
                                                type="number"
                                                variant="outlined"
                                                value={value === 0 && !value ? '' : value}
                                                onChange={onChange}
                                                error={!!error}
                                                helperText={error?.message}
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name="delivery.maxDistance"
                                        control={control}
                                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                                            <TextField
                                                fullWidth
                                                required
                                                label="Límite de distancia (km)"
                                                type="number"
                                                variant="outlined"
                                                value={value === 0 && !value ? '' : value}
                                                onChange={onChange}
                                                error={!!error}
                                                helperText={error?.message}
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end">km</InputAdornment>
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Controller
                                        name="delivery.freeDeliveryEnabled"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={value || false}
                                                        onChange={onChange}
                                                        color="primary"
                                                    />
                                                }
                                                label="Habilitar envío gratis"
                                            />
                                        )}
                                    />
                                </Grid>

                                {freeDeliveryEnabled && (
                                    <Grid item xs={12} md={6}>
                                        <Controller
                                            name="delivery.minAmountForFreeDelivery"
                                            control={control}
                                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                                <TextField
                                                    fullWidth
                                                    required
                                                    label="Monto mínimo para envío gratis"
                                                    type="number"
                                                    variant="outlined"
                                                    value={value === 0 && !value ? '' : value}
                                                    onChange={onChange}
                                                    error={!!error}
                                                    helperText={error?.message}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">$</InputAdornment>
                                                        )
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                )}
                            </>
                        )}

                        <Grid item xs={12}>
                            <Accordion>
                                <AccordionSummary
                                    expandIcon={<ExpandMore />}
                                    aria-controls="schedule-content"
                                    id="schedule-header"
                                >
                                    <Typography variant="body1" fontWeight={600}>
                                        Horario de atención
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        {daysOfWeek.map((day) => (
                                            <Grid item xs={12} key={day.id}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Stack direction="row" flex={1} alignItems="center" spacing={1}>
                                                        <Controller
                                                            name={`schedule.${day.id}.enabled`}
                                                            control={control}
                                                            render={({ field: { onChange, value } }) => (
                                                                <Checkbox
                                                                    checked={value}
                                                                    onChange={onChange}
                                                                    color="primary"
                                                                />
                                                            )}
                                                        />
                                                        <Typography fontWeight={600}>{day.label}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" flex={4} alignItems="center" spacing={2}>
                                                        <Controller
                                                            name={`schedule.${day.id}.openingTime`}
                                                            control={control}
                                                            render={({ field: { onChange, value } }) => (
                                                                <TextField
                                                                    fullWidth
                                                                    label="Hora de apertura"
                                                                    type="time"
                                                                    value={value}
                                                                    onChange={onChange}
                                                                    error={!!errors.schedule?.[day.id]?.openingTime}
                                                                    helperText={
                                                                        errors.schedule?.[day.id]?.openingTime?.message
                                                                    }
                                                                    InputLabelProps={{ shrink: true }}
                                                                />
                                                            )}
                                                        />
                                                        <Typography fontWeight={600}>a</Typography>
                                                        <Controller
                                                            name={`schedule.${day.id}.closingTime`}
                                                            control={control}
                                                            render={({ field: { onChange, value } }) => (
                                                                <TextField
                                                                    fullWidth
                                                                    label="Hora de cierre"
                                                                    type="time"
                                                                    value={value}
                                                                    onChange={onChange}
                                                                    error={!!errors.schedule?.[day.id]?.closingTime}
                                                                    helperText={
                                                                        errors.schedule?.[day.id]?.closingTime?.message
                                                                    }
                                                                    InputLabelProps={{ shrink: true }}
                                                                />
                                                            )}
                                                        />
                                                    </Stack>
                                                </Stack>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle2">Información del responsable de sucursal</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Controller
                                name="manager.name"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        required
                                        label="Nombre"
                                        variant="outlined"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.manager?.name}
                                        helperText={errors.manager?.name && errors.manager?.name.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Controller
                                name="manager.phone"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        required
                                        label="Teléfono"
                                        variant="outlined"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.manager?.phone}
                                        helperText={errors.manager?.phone && errors.manager?.phone.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Controller
                                name="manager.email"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        required
                                        label="Correo"
                                        variant="outlined"
                                        value={value}
                                        onChange={onChange}
                                        error={!!errors.manager?.email}
                                        helperText={errors.manager?.email && errors.manager?.email.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="notifyManagerOnNewOrder"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={value ?? true}
                                                onChange={onChange}
                                                color="primary"
                                            />
                                        }
                                        label="Notificar nuevos pedidos por WhatsApp al responsable"
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="inventoryDiscountEnabled"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={value ?? false}
                                                onChange={onChange}
                                                disabled={lockProtected}
                                                color="primary"
                                            />
                                        }
                                        label="Descontar inventario con cada venta"
                                    />
                                )}
                            />
                            {/* F5 §4.2: se enciende sucursal por sucursal DESPUÉS de su
                                conteo físico, para que el ledger arranque de un saldo
                                real. Apagado, las ventas se explotan y se registran
                                pero no tocan el stock. */}
                            <Typography variant="caption" color="text.secondary" display="block">
                                Enciéndelo solo después del conteo físico de esta sucursal. Apagado, las ventas no
                                modifican el stock.
                            </Typography>
                        </Grid>
                        {/* Plan de inventario F4: el estado de cada insumo se mide en DÍAS al ritmo de
                            esta sucursal; estos dos umbrales los decide el admin de sucursal (no son
                            campos protegidos). */}
                        <Grid item xs={6}>
                            <Controller
                                name="stockDays.critical"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Crítico: menos de … días de inventario"
                                        variant="outlined"
                                        inputProps={{ min: 0, step: 'any', inputMode: 'decimal' }}
                                        value={value ?? ''}
                                        onChange={onChange}
                                        error={!!errors.stockDays?.critical}
                                        helperText={errors.stockDays?.critical?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Controller
                                name="stockDays.low"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Bajo: menos de … días de inventario"
                                        variant="outlined"
                                        inputProps={{ min: 0, step: 'any', inputMode: 'decimal' }}
                                        value={value ?? ''}
                                        onChange={onChange}
                                        error={!!errors.stockDays?.low}
                                        helperText={errors.stockDays?.low?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                El inventario dice cuántos días le dura cada insumo a esta sucursal al ritmo al que lo gasta.
                                Con estos umbrales se pinta Crítico y Bajo. Propuesta: 2 y 5 (el CEDIS surte cada 3–4 días).
                            </Typography>
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>
            <DialogActions>
                <Box flex={1} textAlign="center">
                    <Button
                        sx={{ width: 300 }}
                        color="primary"
                        variant="contained"
                        type="submit"
                        form="add-branch-office-form"
                    >
                        Guardar
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};
